import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LiveIndicator } from '@sorare/core/src/atoms/ui/LiveIndicator';
import { gameStatusMessages, glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { isGameFinished, isGameLive } from 'shared/game/lib';

import type { GameStatusLabel_anyGameInterface } from './__generated__/index.graphql';

type Props = {
  game: GameStatusLabel_anyGameInterface;
  dateFormat?: Intl.DateTimeFormatOptions;
};

export const GameStatusLabel = ({
  game,
  dateFormat = { year: 'numeric' },
}: Props) => {
  const date = (
    <FormattedDate
      value={game.date}
      month="short"
      day="numeric"
      hour="2-digit"
      minute="2-digit"
      {...dateFormat}
    />
  );

  return (
    <>
      {isGameFinished(game.statusTyped) ? (
        <FormattedMessage {...gameStatusMessages.played} />
      ) : (
        <>
          {isGameLive(game.statusTyped) ? (
            <Horizontal as="span">
              <LiveIndicator />
              {isType(game, 'Game') ? (
                <>{game.minute}&lsquo;</>
              ) : (
                <>
                  {isType(game, 'GameOfBasketball') ? (
                    <>
                      {game.isHalftime ? (
                        <FormattedMessage
                          id="Game.status.halftime"
                          defaultMessage="Half"
                        />
                      ) : (
                        <FormattedMessage
                          {...glossary.ordinal}
                          values={{ ordinal: game.quarter }}
                        />
                      )}
                    </>
                  ) : (
                    <FormattedMessage {...gameStatusMessages.live} />
                  )}
                </>
              )}
            </Horizontal>
          ) : (
            date
          )}
        </>
      )}
    </>
  );
};

GameStatusLabel.fragments = {
  anyGameInterface: gql`
    fragment GameStatusLabel_anyGameInterface on AnyGameInterface {
      id
      statusTyped
      date
      ... on Game {
        id
        minute
      }
      ... on GameOfBasketball {
        id
        isHalftime
        quarter
      }
    }
  ` as TypedDocumentNode<GameStatusLabel_anyGameInterface>,
};
